import { useMemo } from 'react'
import type { Dispatch } from 'react'
import type { ShopMessageKey } from '../../i18n'
import type { CheckoutAddress, CheckoutStep } from '../types'
import type { CheckoutFlowAction, CheckoutFlowErrorCode, CheckoutFlowState } from './CheckoutFlow.types'
import type { UseCheckoutSubmitHandlersOutput } from './useCheckoutSubmitHandlers'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseCheckoutViewModelInput = {
  state: CheckoutFlowState
  dispatch: Dispatch<CheckoutFlowAction>
  formatCurrency: (value: number) => string
  t: TranslateFn
  submitHandlers: UseCheckoutSubmitHandlersOutput
}

type ProgressStep = {
  step: CheckoutStep
  label: string
  order: number
  isCurrent: boolean
  isCompleted: boolean
}

const formatAddressSummary = (address: CheckoutAddress | null): string => {
  if (!address) return '-'
  const chunks = [
    `${address.firstName} ${address.lastName}`.trim(),
    address.line1,
    address.line2,
    `${address.postalCode} ${address.city}`.trim(),
    address.region,
    address.country,
    address.phone,
  ]
  return chunks.filter((chunk): chunk is string => Boolean(chunk && chunk.trim())).join(', ')
}

const resolveProgressOrder = (step: CheckoutStep): number => {
  if (step === 'cart_review') return 1
  if (step === 'shipping_address') return 2
  if (step === 'billing_address') return 3
  if (step === 'shipping_method') return 4
  if (step === 'review') return 5
  if (step === 'redirecting_to_payment' || step === 'completed') return 6
  return 1
}

const resolveErrorMessage = ({
  errorCode,
  errorDetail,
  t,
}: {
  errorCode: CheckoutFlowErrorCode | null
  errorDetail: string
  t: TranslateFn
}) => {
  if (!errorCode) return null

  const detail = errorDetail.trim()
  if (errorCode === 'session_creation_failed') return detail || t('checkout.error.sessionCreationFailed')
  if (errorCode === 'session_load_failed') return detail || t('checkout.error.sessionLoadFailed')
  if (errorCode === 'session_expired') return detail || t('checkout.error.sessionExpired')
  if (errorCode === 'empty_cart') return detail || t('checkout.error.emptyCart')
  if (errorCode === 'invalid_step') return t('checkout.error.invalidStep')
  if (errorCode === 'confirm_payment_failed') return detail || t('checkout.error.confirmPaymentFailed')
  if (errorCode === 'backend_validation_error') return detail || t('checkout.error.backendValidation')
  return detail || t('checkout.error.generic')
}

export const resolveIsEmptyCheckoutState = (state: CheckoutFlowState): boolean => {
  const resolvedCartItems = state.review?.cartSnapshot.items ?? state.session?.cartSnapshot.items ?? []
  if (state.isBootstrapping) return false
  if (state.errorCode === 'empty_cart') return true
  return Boolean(state.session) && resolvedCartItems.length === 0
}

export const useCheckoutViewModel = ({
  state,
  dispatch,
  formatCurrency,
  t,
  submitHandlers,
}: UseCheckoutViewModelInput) => {
  const validationByField = useMemo(
    () => new Map(state.validationErrors.map((entry) => [entry.field, entry.message])),
    [state.validationErrors],
  )

  const totals = state.review?.totalsSnapshot ?? state.session?.totalsSnapshot ?? null
  const shippingMethods = state.shippingMethods.length > 0
    ? state.shippingMethods
    : [
        {
          code: 'standard' as const,
          label: t('checkout.shippingMethod.standard.label'),
          description: t('checkout.shippingMethod.standard.description'),
          amount: 0,
          currency: 'EUR' as const,
        },
        {
          code: 'express' as const,
          label: t('checkout.shippingMethod.express.label'),
          description: t('checkout.shippingMethod.express.description'),
          amount: 0,
          currency: 'EUR' as const,
        },
      ]
  const paymentProviders = state.paymentProviders.length > 0
    ? state.paymentProviders
    : [
        {
          id: 'bank_transfer' as const,
          label: t('checkout.payment.provider.bankTransfer'),
          description: t('checkout.payment.provider.bankTransferDescription'),
          enabled: true,
          requiresRedirect: false,
          supportsWebhook: false,
          supportsManualSettlement: true,
        },
        {
          id: 'stripe' as const,
          label: t('checkout.payment.provider.stripe'),
          description: t('checkout.payment.provider.stripeDescription'),
          enabled: true,
          requiresRedirect: true,
          supportsWebhook: true,
          supportsManualSettlement: false,
        },
      ]

  const currentOrder = resolveProgressOrder(state.step)
  const selectedProviderConfig = paymentProviders.find((provider) => provider.id === state.selectedPaymentProvider)

  const progressSteps: ProgressStep[] = [
    { step: 'cart_review', label: t('checkout.step.cart'), order: 1, isCurrent: currentOrder === 1, isCompleted: currentOrder > 1 },
    { step: 'shipping_address', label: t('checkout.step.shipping'), order: 2, isCurrent: currentOrder === 2, isCompleted: currentOrder > 2 },
    { step: 'billing_address', label: t('checkout.step.billing'), order: 3, isCurrent: currentOrder === 3, isCompleted: currentOrder > 3 },
    { step: 'shipping_method', label: t('checkout.step.method'), order: 4, isCurrent: currentOrder === 4, isCompleted: currentOrder > 4 },
    { step: 'review', label: t('checkout.step.review'), order: 5, isCurrent: currentOrder === 5, isCompleted: currentOrder > 5 },
    { step: 'redirecting_to_payment', label: t('checkout.step.payment'), order: 6, isCurrent: currentOrder === 6, isCompleted: false },
  ]

  const errorMessage = resolveErrorMessage({
    errorCode: state.errorCode,
    errorDetail: state.errorDetail,
    t,
  })
  const resolvedCartItems = state.review?.cartSnapshot.items ?? state.session?.cartSnapshot.items ?? []
  const isEmptyCartState = resolveIsEmptyCheckoutState(state)

  return {
    isBusy: state.isBootstrapping || state.isSubmitting,
    isRedirecting: state.isRedirecting,
    step: state.step,
    session: state.session,
    review: state.review,
    shippingMethods,
    paymentProviders,
    sameAsShipping: state.sameAsShipping,
    selectedShippingMethod: state.selectedShippingMethod,
    selectedPaymentProvider: state.selectedPaymentProvider,
    shippingAddressForm: state.shippingAddressForm,
    billingAddressForm: state.billingAddressForm,
    errorCode: state.errorCode,
    errorDetail: state.errorDetail,
    errorMessage,
    validationByField,
    totals,
    totalsRows: totals
      ? [
          { label: t('checkout.review.subtotal'), value: formatCurrency(totals.subtotal) },
          { label: t('checkout.review.shipping'), value: formatCurrency(totals.shipping) },
          { label: t('checkout.review.tax'), value: formatCurrency(totals.tax) },
          { label: t('checkout.review.discount'), value: formatCurrency(totals.discount) },
          { label: t('checkout.review.total'), value: formatCurrency(totals.grandTotal), strong: true },
        ]
      : [],
    shippingSummary: formatAddressSummary(state.review?.shippingAddress ?? state.session?.shippingAddress ?? null),
    billingSummary: formatAddressSummary(state.review?.billingAddress ?? state.session?.billingAddress ?? null),
    shippingMethodSummary: state.review?.shippingMethod?.label ?? state.session?.shippingMethod?.label ?? t('checkout.review.notSelected'),
    bankTransferInstructions:
      state.lastConfirmResult?.bankTransferInstructions ?? state.session?.bankTransferInstructions ?? null,
    paymentProviderLabel:
      selectedProviderConfig?.label
      ?? state.selectedPaymentProvider,
    selectedPaymentProviderRequiresRedirect: selectedProviderConfig?.requiresRedirect ?? true,
    cartItems: resolvedCartItems,
    isEmptyCartState,
    progressSteps,
    currentProgressOrder: currentOrder,
    setSameAsShipping: (value: boolean) => {
      dispatch({ type: 'SET_SAME_AS_SHIPPING', value })
    },
    setShippingField: (field: keyof CheckoutFlowState['shippingAddressForm'], value: string) => {
      dispatch({ type: 'SET_SHIPPING_FIELD', field, value })
    },
    setShippingForm: (value: CheckoutFlowState['shippingAddressForm']) => {
      dispatch({ type: 'SET_SHIPPING_FORM', value })
    },
    setBillingField: (field: keyof CheckoutFlowState['billingAddressForm'], value: string) => {
      dispatch({ type: 'SET_BILLING_FIELD', field, value })
    },
    setBillingForm: (value: CheckoutFlowState['billingAddressForm']) => {
      dispatch({ type: 'SET_BILLING_FORM', value })
    },
    setShippingMethod: (value: 'standard' | 'express') => {
      dispatch({ type: 'SET_SELECTED_SHIPPING_METHOD', value })
    },
    setPaymentProvider: (value: 'stripe' | 'bank_transfer') => {
      dispatch({ type: 'SET_SELECTED_PAYMENT_PROVIDER', value })
    },
    clearError: () => {
      dispatch({ type: 'CLEAR_ERROR' })
    },
    canRetryError: state.errorCode !== 'invalid_step' && state.errorCode !== null,
    canGoBackToCart: state.errorCode !== null,
    ...submitHandlers,
  }
}
